import { ENABLE_MAINTENANCE_MODE } from './index';
import { lazyImport } from '../utils/lazyImport/lazyImport';

/**
 *
 * interface IRoute {
 *   path: string;
 *   Component?: React.LazyExoticComponent<React.ReactNode>;
 *   private?: boolean;
 *   queryArguments?: string[];
 *   exact?: boolean; // Exact Match für die Route
 *   sorting?: number; // Sortierung der Route
 *   redirects?: string[]; // Redirects welche auf die Route Umleiten
 *   tinted?: boolean; // Menu wird gefärbt im initialen Zustand
 *   theme?: 'calm' | 'podcast' | 'work'; // Theme für die Seite
 *   hideFooterLinks?: boolean; // Footer Links werden nicht angezeigt
 *   cooperation?: boolean; // Kooperation mit Krankenkasse
 *   disableAuthentication?: boolean; // Authentifizierung wird deaktiviert
 *   cooperationColor?: 'green' | 'blue'; // Farbe für die Kooperation
 *   hideMenuLinks: boolean; // Footer wird angezeigt
 *   hideFooter: boolean; // Footer wird angezeigt
 * }
 *
 * @typedef {Object.<string, IRoute>} Routes
 */

export let Routes = {
  home: {
    exact: true,
    path: '/',
    Component: lazyImport(() => import('components/Home/Home'))
  },
  appLegacyImprint: {
    path: '/app-resources/managed-2/HTML/imprint/imprint.html',
    Component: lazyImport(() => import('Page/AppImprintPage/AppImprintPage'))
  },
  appLegacyPrivacy: {
    path: '/app-resources/managed-2/HTML/terms/privacy.html',
    Component: lazyImport(() => import('Page/AppPrivacyPage/AppPrivacyPage'))
  },
  appLegacyTerms: {
    path: '/app-resources/managed-2/HTML/terms/terms/terms.html',
    Component: lazyImport(() => import('Page/AppTermsPage/AppTermsPage'))
  },
  imprint: {
    path: '/imprint',
    Component: lazyImport(() => import('components/Imprint'))
  },
  buyGift: {
    path: '/verschenken',
    tinted: true,
    Component: lazyImport(() => import('Page/Gift/BuyGift'))
  },
  checkout: {
    path: '/checkout',
    tinted: true,
    Component: lazyImport(() => import('Page/Checkout/Checkout'))
  },
  checkoutConfirm: {
    path: '/checkout-confirm',
    Component: lazyImport(() => import('Page/CheckoutConfirm/CheckoutConfirm'))
  },
  podcast: {
    path: '/podcast',
    tinted: true,
    theme: 'podcast',
    Component: lazyImport(() => import('components/Landingpages/Podcast/PodcastLandingPage'))
  },
  plans: {
    path: '/buy',
    tinted: true,
    Component: lazyImport(() => import('Page/PurchasePage/PurchasePage'))
  },
  category: {
    path: '/category/:slug',
    queryArguments: ['slug'],
    tinted: true,
    Component: lazyImport(() => import('containers/Library/CategoryContainer'))
  },
  library: {
    path: '/bibliothek',
    tinted: true,
    Component: lazyImport(() => import('containers/Library/LibraryContainer'))
  },
  singlePlayer: {
    path: '/play/single/:slug',
    tinted: true,
    queryArguments: ['slug'],
    Component: lazyImport(() => import('Page/SinglePlayerPage/SinglePlayerPage'))
  },
  webplayer: {
    path: '/play/course/:slug',
    tinted: true,
    queryArguments: ['slug'],
    Component: lazyImport(() => import('Page/CoursePlayerPage/CoursePlayerPage'))
  },
  thankyou: {
    path: '/thankyou',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYou')),
    private: true
  },
  thankyougift: {
    path: '/thankyougift',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouGift'))
  },
  thankyouloggedout: {
    path: '/thankyouloggedout',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouLoggedOut'))
  },
  thankyoupurchaseassociated: {
    path: '/thankyoupurchaseassociated',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouPurchaseAssociated'))
  },
  thankyouupgrade: {
    path: '/thankyouupgrade',
    tinted: true,
    Component: lazyImport(() => import('Page/ThankYou/ThankYouUpgrade')),
    private: true
  },
  externalLegacyFacebookPrivacy: {
    path: '/facebook-privacy',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalFacebookPrivacyPage/ExternalFacebookPrivacyPage'))
  },
  externalLegacyPrivacyPolicy: {
    path: '/privacy-policy',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalPrivacyPolicyPage/ExternalPrivacyPolicyPage'))
  },
  externalLegacyTerms: {
    path: '/terms',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalTermsPage/ExternalTermsPage'))
  },
  externalLegacyHealth: {
    path: '/health',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/ExternalHealthPage/ExternalHealthPage'))
  },
  externalFacebookPrivacy: {
    path: '/legal/facebook-privacy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalFacebookPrivacyPage/ExternalFacebookPrivacyPage'))
  },
  externalPrivacyPolicy: {
    path: '/legal/privacy-policy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalPrivacyPolicyPage/ExternalPrivacyPolicyPage'))
  },
  externalTerms: {
    path: '/legal/terms',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalTermsPage/ExternalTermsPage'))
  },
  externalHealth: {
    path: '/legal/health',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalHealthPage/ExternalHealthPage'))
  },
  webPrivacy: {
    path: '/legal/web/privacy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/PrivacyPage/PrivacyPage'))
  },
  webTerms: {
    path: '/legal/web/terms',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/TermsPage/TermsPage'))
  },
  webHealth: {
    path: '/legal/web/health',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/HealthPage/HealthPage'))
  },
  webHealthBenefit: {
    path: '/legal/web/health-benefit',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/HealthBenefitPage/HealthBenefitPage'))
  },
  webAccessibility: {
    path: '/legal/web/accessibility',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/AccessibilityPage/AccessibilityPage'))
  },
  login: {
    path: '/login',
    tinted: true,
    Component: lazyImport(() => import('Page/AuthenticationPage/AuthenticationPage'))
  },
  signup: {
    path: '/signup',
    tinted: true,
    Component: lazyImport(() => import('Page/AuthenticationPage/AuthenticationPage'))
  },
  confirmMail: {
    path: '/web/login',
    queryArguments: ['code'],
    tinted: true,
    Component: lazyImport(() => import('components/SignInUserWithOnetimePasswordBox'))
  },
  confirmMailCode: {
    path: '/web/login/:code?',
    queryArguments: ['code'],
    tinted: true,
    Component: lazyImport(() => import('components/SignInUserWithOnetimePasswordBox'))
  },
  fallbackAppOtp: {
    path: '/app/login/:code?',
    queryArguments: ['code'],
    Component: lazyImport(() => import('components/FallbackAppOtpPage'))
  },
  paymenterror: {
    path: '/paymenterror',
    tinted: true,
    Component: lazyImport(() => import('Page/Checkout/PaymentError'))
  },
  paymenterror_nostorage: {
    path: '/payment-referererror',
    Component: lazyImport(() => import('Page/Checkout/PaymentErrorNoStorage'))
  },
  upgradeerror: {
    path: '/upgradeerror',
    Component: lazyImport(() => import('components/SubscriptionUpgrade/UpgradeError'))
  },
  email_confirmation: {
    path: '/email_confirmation',
    tinted: true,
    Component: lazyImport(() => import('components/EmailConfirmation'))
  },
  email_confirmation_error: {
    path: '/email_confirmation_error',
    Component: lazyImport(() => import('components/EmailConfirmationError'))
  },
  upgrade: {
    path: '/upgrade',
    tinted: true,
    Component: lazyImport(() => import('Page/SubscriptionUpgradePage/SubscriptionUpgradePage'))
  },
  withdrawCancelation: {
    path: '/withdraw-cancelation',
    Component: lazyImport(() => import('containers/Checkout/WithdrawCancelationContainer'))
  },
  company: {
    path: '/unternehmen',
    theme: 'work',
    Component: lazyImport(() => import('components/Landingpages/B2B/B2BLandingPage'))
  },
  terminateSuccess: {
    path: '/terminate-success',
    tinted: true,
    Component: lazyImport(() => import('Page/TerminateUserAboSuccess/TerminateUserAboSuccess'))
  },
  terminateFailed: {
    path: '/terminate-failed',
    tinted: true,
    Component: lazyImport(() => import('Page/TerminateUserAboFailed/TerminateUserAboFailed'))
  },
  terminate: {
    path: '/terminate',
    tinted: true,
    Component: lazyImport(() => import('Page/TerminateUserAbo/TerminateUserAbo'))
  },
  fallbackApp: {
    path: '/app/*',
    tinted: true,
    Component: lazyImport(() => import('components/FallbackAppPage'))
  },
  fallbackAppView: {
    path: '/view/*',
    Component: lazyImport(() => import('components/FallbackAppPage'))
  },
  krankenkasseSurveyWrong: {
    path: '/krankenkasse/survey-wrong',
    hideMenuLinks: true,
    Component: lazyImport(() => import('components/Landingpages/HealthInsurance/Survey/Wrong'))
  },
  krankenkasseSurveyRight: {
    path: '/krankenkasse/survey-right',
    hideMenuLinks: true,
    Component: lazyImport(() => import('components/Landingpages/HealthInsurance/Survey/Right'))
  },
  krankenkasseSurveyMaybe: {
    path: '/krankenkasse/survey-maybe',
    hideMenuLinks: true,
    Component: lazyImport(() => import('components/Landingpages/HealthInsurance/Survey/Maybe'))
  },
  krankenkasse: {
    tinted: true,
    sorting: 500,
    path: '/krankenkasse',
    Component: lazyImport(() => import('components/Landingpages/PreventionPromo/PreventionPromoPage'))
  },
  stressreduktion: {
    path: '/stressreduktion',
    cooperation: true,
    hideMenuLinks: true,
    disableAuthentication: true,
    cooperationColor: 'green',
    Component: lazyImport(() => import('components/Landingpages/HealthInsuranceMarketing/HealthInsuranceMarketingPage'))
  },
  healthAdvisor: {
    path: '/health-advisor',
    Component: lazyImport(() => import('components/Landingpages/HealthAdvisorPage/HealthAdvisorPage'))
  },
  trustPortal: {
    path: '/security',
    Component: lazyImport(() => import('Page/TrustPortalPage/TrustPortalPage'))
  },
  externalPrivacy: {
    path: '/legal/privacy',
    tinted: true,
    hideFooterLinks: true,
    Component: lazyImport(() => import('Page/ExternalFacebookPrivacyPage/ExternalFacebookPrivacyPage'))
  },
  webappLegacyAccessibility: {
    path: '/webapp-resources/health_accessibility.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/AccessibilityPage/AccessibilityPage'))
  },
  webappLegacyHealth: {
    path: '/webapp-resources/health.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/HealthPage/HealthPage'))
  },
  webappLegacyHealthBenefit: {
    path: '/webapp-resources/health_benefit.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/HealthBenefitPage/HealthBenefitPage'))
  },
  webappLegacyPrivacy: {
    path: '/webapp-resources/privacy.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/PrivacyPage/PrivacyPage'))
  },
  webappLegacyTerms: {
    path: '/webapp-resources/terms.html',
    hideFooterLinks: true,
    tinted: true,
    Component: lazyImport(() => import('Page/TermsPage/TermsPage'))
  },
  insuranceCooperationAuthentication: {
    hideFooterLinks: true,
    path: '/insurance-cooperation-authentication',
    Component: lazyImport(() => import('Page/InsuranceCooperationAuthenticationPage/InsuranceCooperationAuthenticationPage'))
  },

  resilience: {
    path: '/krankenkasse/resilienz',
    hideMenuLinks: true,
    tinted: true,
    theme: 'calm',
    Component: lazyImport(() => import('components/Landingpages/ResiliencePage/ResiliencePage'))
  },
  sda: {
    path: '/krankenkasse/sda',
    hideMenuLinks: true,
    tinted: true,
    Component: lazyImport(() => import('components/Landingpages/SdaPage/SdaPage'))
  },

  bkk: {
    path: '/krankenkasse/bertelsmann-bkk',
    cooperation: true,
    hideMenuLinks: true,
    theme: 'calm',
    exact: true,
    redirects: ['/bertelsmann-bkk'],
    Component: lazyImport(() => import('components/Landingpages/Bkk/BkkLandingPage'))
  },
  gothaer: {
    cooperation: true,
    hideMenuLinks: true,
    path: '/krankenkasse/gothaer',
    exact: true,
    theme: 'calm',
    redirects: ['/gothaer'],
    Component: lazyImport(() => import('components/Landingpages/Gothaer/GothaerLandingPage'))
  },
  dakBgmHealthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    exact: true,
    redirects: ['/dak-bgm'],
    path: '/krankenkasse/dak-bgm',
    Component: lazyImport(() => import('Page/HealthInsuranceDakBgmCooperationOfferPage/HealthInsuranceDakBgmCooperationOfferPage'))
  },
  hkkBgmHealthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    exact: true,
    path: '/krankenkasse/hkk-bgm',
    Component: lazyImport(() => import('Page/HealthInsuranceHkkBgmCooperationOfferPage/HealthInsuranceHkkBgmCooperationOfferPage'))
  },
  healthInsuranceCooperation: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    cooperation: true,
    exact: true,
    path: '/krankenkasse/:cooperation',
    queryArguments: ['cooperation'],
    Component: lazyImport(() => import('Page/HealthInsuranceCooperationPartnerPage/HealthInsuranceCooperationPartnerPage'))
  },

  tkHealthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    theme: 'calm',
    exact: true,
    path: '/krankenkasse/techniker/:course',
    queryArguments: ['course'],
    Component: lazyImport(() => import('Page/HealthInsuranceTkCooperationOfferPage/HealthInsuranceTkCooperationOfferPage'))
  },
  healthInsuranceCooperationCourse: {
    hideMenuLinks: true,
    disableAuthentication: true,
    cooperation: true,
    cooperationColor: 'blue',
    theme: 'calm',
    exact: true,
    path: '/krankenkasse/:cooperation/:course',
    queryArguments: ['cooperation', 'course'],
    Component: lazyImport(() => import('Page/HealthInsuranceCooperationOfferPage/HealthInsuranceCooperationOfferPage'))
  },

  profile: {
    path: '/profile/*',
    tinted: true,
    private: true,
    Component: lazyImport(() => import('components/Profile/Profile'))
  },
  profilePersonalData: {
    path: '/profile/personal-data',
    tinted: true,
    private: true,
    Component: lazyImport(() => import('components/Profile/Profile'))
  },
  profileSubscriptionStatus: {
    path: '/profile/subscription-status',
    tinted: true,
    private: true,
    Component: lazyImport(() => import('components/Profile/Profile'))
  },
  voucher_manual: {
    path: '/gutschein/anleitung',
    tinted: true,
    Component: lazyImport(() => import('components/Landingpages/VoucherManual/VoucherManual'))
  },
  voucher: {
    path: '/gutschein',
    private: true,
    tinted: true,
    Component: lazyImport(() => import('components/Profile/Profile'))
  },
  deeplinkConfirmMail: {
    path: '/web/confirm_email_change/:code?',
    queryArguments: ['code'],
    Component: lazyImport(() => import('components/Profile/Profile'))
  },
  maintenance: {
    path: '/wartung',
    Component: lazyImport(() => import('components/Application/Maintenance/Maintenance')),
    hideFooter: true,
    tinted: true,
    hideMenuLinks: true
  }
};

if (ENABLE_MAINTENANCE_MODE) {
  Routes = {
    maintenance: {
      path: '/wartung',
      Component: lazyImport(() => import('components/Application/Maintenance/Maintenance')),
      hideFooter: true,
      tinted: true,
      hideMenuLinks: true
    }
  };
}
